import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, Globe, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import OfferEditor, { emptyDraft, type OfferDraft } from '@/components/offers/OfferEditor';
import PackageCard from '@/components/offers/PackageCard';
import { useProcessingFee } from '@/hooks/useProcessingFee';
import { apiError } from '@/lib/api-error';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { ratePercent } from '@/types/advisor-billing';
import { type Offer } from '@/types/offer';

export default function PublicOffersPage() {
  const { data: fee } = useProcessingFee();
  const feeRate = fee && parseFloat(fee.rate_percent) > 0 ? ratePercent(fee.rate_percent) : null;
  const feeLabel = feeRate ? `${feeRate} platform fee` : 'No platform fee';
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<OfferDraft | null>(null);

  const { data: offers, isLoading } = useQuery<Offer[]>({
    queryKey: ['public-offers'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/advisor/offers/', { params: { public: 1 } });
      return res.data?.results ?? res.data ?? [];
    },
  });

  const { data: requests } = useQuery<Offer[]>({
    queryKey: ['requested-offers'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/advisor/offers/', { params: { requested: 1 } });
      return res.data?.results ?? res.data ?? [];
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['public-offers'] });
    queryClient.invalidateQueries({ queryKey: ['requested-offers'] });
  };

  const create = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post('/advisor/offers/', { ...draft, is_public: true, client: null });
    },
    onSuccess: () => { toast.success('Saved. Publish it when it is ready.'); setDraft(null); refresh(); },
    onError: (error) => toast.error(apiError(error, 'Could not save the offer.')),
  });

  const act = useMutation({
    mutationFn: async ({ id, what }: { id: string; what: 'send' | 'withdraw' }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/advisor/offers/${id}/${what}/`);
    },
    onSuccess: (_data, { what }) => {
      toast.success(what === 'send' ? 'Published on your profile.' : 'Taken off your profile.');
      refresh();
    },
    onError: (error) => toast.error(apiError(error, 'That did not work.')),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.delete(`/advisor/offers/${id}/`);
    },
    onSuccess: () => { toast.success('Deleted.'); refresh(); },
    onError: (error) => toast.error(apiError(error, 'Could not delete it.')),
  });

  const open = (requests ?? []).filter((offer) => offer.status === 'requested');

  return (
    <div className="space-y-4" data-testid="public-offers-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Public offers</h2>
          <p className="text-muted-foreground">
            Packages anyone can see on your public profile and ask for the moment they choose you.
            A request is not a contract until you confirm it, so you can always turn one down with a reason.
          </p>
        </div>
        {!draft && (
          <Button onClick={() => setDraft(emptyDraft())} data-testid="new-public-offer-btn">
            <Plus className="mr-2 h-4 w-4" />
            New public offer
          </Button>
        )}
      </div>

      {open.length > 0 && (
        <Card className="border-sky-300 bg-sky-50/40" data-testid="public-offer-requests">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{open.length} request{open.length === 1 ? '' : 's'} waiting for your answer</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            {open.map((offer) => (
              <div key={offer.id} className="flex flex-wrap items-center justify-between gap-2">
                <span><strong>{offer.company_name}</strong> asked for {offer.title}{offer.packages.find((p) => p.id === offer.accepted_package)?.name ? `, ${offer.packages.find((p) => p.id === offer.accepted_package)!.name}` : ''}.</span>
                <Link to={`/advisor/clients/${offer.client}/offers`} className="text-xs underline underline-offset-4" data-testid={`open-request-${offer.id}`}>
                  Confirm or turn down
                </Link>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {draft && (
        <OfferEditor
          draft={draft}
          setDraft={setDraft}
          onSave={() => create.mutate()}
          onCancel={() => setDraft(null)}
          saving={create.isPending}
          feeLabel={feeLabel}
          heading="A new public offer"
          saveLabel="Save"
        />
      )}

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (offers ?? []).length === 0 && !draft ? (
        <p className="py-12 text-center text-sm text-muted-foreground" data-testid="public-offers-empty">
          Nothing on your profile yet. A public offer is the fastest way for a new client to start: they pick a package, you confirm, and the contract is running.
        </p>
      ) : (
        <div className="space-y-4" data-testid="public-offer-list">
          {(offers ?? []).map((offer) => (
            <Card key={offer.id} data-testid={`public-offer-${offer.id}`}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{offer.title}</CardTitle>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {offer.billing_mode === 'via_aoc' ? 'Billed through BitSwan' : 'You invoice directly'} · {offer.currency}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={offer.status === 'sent' ? 'bg-emerald-500/15 text-emerald-700' : 'bg-muted text-muted-foreground'} data-testid={`public-offer-status-${offer.id}`}>
                      {offer.status === 'sent' ? 'On your profile' : 'Not shown'}
                    </Badge>
                    {offer.status === 'sent' ? (
                      <Button size="sm" variant="outline" onClick={() => act.mutate({ id: offer.id, what: 'withdraw' })} disabled={act.isPending} data-testid={`unpublish-offer-${offer.id}`}>
                        <EyeOff className="mr-2 h-3.5 w-3.5" /> Take off profile
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => act.mutate({ id: offer.id, what: 'send' })} disabled={act.isPending} data-testid={`publish-offer-${offer.id}`}>
                        <Eye className="mr-2 h-3.5 w-3.5" /> Publish
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => remove.mutate(offer.id)} data-testid={`delete-public-offer-${offer.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {offer.introduction && <p className="text-sm text-muted-foreground">{offer.introduction}</p>}
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {offer.packages.map((pkg) => (
                    <PackageCard key={pkg.id} pkg={pkg} currency={offer.currency} testId={`public-offer-package-${pkg.id}`} />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Globe className="h-3.5 w-3.5" /> Published offers appear on your public profile under your bio.
      </p>
    </div>
  );
}
